import React, { useMemo } from 'react';
import { ResumePreviewProps, WorkExperience, Education, Certification, Project } from '../types';
import { usePreviewSettings } from '../templateSettings';

const formatDateWithFormat = (dateString: string | null, dateFormat?: string, yearOnly?: boolean): string => {
  if (!dateString) return '';
  const date = /^\d{4}-\d{2}$/.test(dateString) ? new Date(`${dateString}-01`) : new Date(dateString);
  if (isNaN(date.getTime())) return "";
  if (yearOnly) return date.getFullYear().toString();
  if (!dateFormat) {
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const year = date.getFullYear().toString().slice(-2);
    return `${month} '${year}`;
  }
  const month = date.getMonth() + 1;
  const monthShort = date.toLocaleDateString('en-US', { month: 'short' });
  const monthLong = date.toLocaleDateString('en-US', { month: 'long' });
  const year = date.getFullYear();
  const yearShort = year.toString().slice(-2);
  switch (dateFormat) {
    case '%b %y': return `${monthShort} ${yearShort}`;
    case '%B %Y': return `${monthLong} ${year}`;
    case '%b %Y': return `${monthShort} ${year}`;
    case '%m/%Y': return `${month.toString().padStart(2, '0')}/${year}`;
    case '%m/%y': return `${month.toString().padStart(2, '0')}/${yearShort}`;
    case '%Y': return year.toString();
    default: return `${monthShort} '${yearShort}`;
  }
};

const formatDateRange = (startDate: string | null, endDate: string | null, isCurrent: boolean, dateFormat?: string, yearOnly?: boolean, hideDate?: boolean): string => {
  if (hideDate) return '';
  if (!startDate) return '';
  const start = formatDateWithFormat(startDate, dateFormat, yearOnly);
  if (isCurrent) return `${start} - Present`;
  if (!endDate) return start;
  const end = formatDateWithFormat(endDate, dateFormat, yearOnly);
  return `${start} - ${end}`;
};

interface EditableFieldProps {
  fieldName: string;
  children: React.ReactNode;
  activeField?: string | null;
  onFieldClick?: (fieldName: string) => void;
  className?: string;
}

const EditableField: React.FC<EditableFieldProps> = ({
  fieldName,
  children,
  activeField,
  onFieldClick,
  className = ''
}) => {
  const isActive = activeField === fieldName;
  return (
    <span
      className={`resume-editable-field ${isActive ? 'resume-editable-field--active' : ''} ${className}`}
      onClick={(e) => {
        e.stopPropagation();
        onFieldClick?.(fieldName);
      }}
    >
      {children}
    </span>
  );
};

interface EditableBlockProps {
  fieldName: string;
  children: React.ReactNode;
  activeField?: string | null;
  onFieldClick?: (fieldName: string) => void;
  className?: string;
}

const EditableBlock: React.FC<EditableBlockProps> = ({
  fieldName,
  children,
  activeField,
  onFieldClick,
  className = ''
}) => {
  const isActive = activeField === fieldName;
  return (
    <div
      className={`resume-editable-block ${isActive ? 'resume-editable-block--active' : ''} ${className}`}
      onClick={(e) => {
        e.stopPropagation();
        onFieldClick?.(fieldName);
      }}
    >
      {children}
    </div>
  );
};

export const BonsaiTemplate: React.FC<ResumePreviewProps> = ({ data, activeField, onFieldClick, sectionOrder, structures = [], settings }) => {
  const {
    personalInfo,
    targetJobTitle,
    professionalSummary,
    workExperiences,
    volunteerExperiences,
    educations,
    certifications,
    projects,
    categorySkills,
    links,
    customSections
  } = data;

  const dateFormat = settings?.date_format;
  const { rootStyle } = usePreviewSettings(settings);

  const structureTypeToId: Record<string, string> = {
    'TYPE_INFO': 'about',
    'TYPE_WORK_EXPERIENCE': 'experience',
    'TYPE_VOLUNTEER_EXPERIENCE': 'volunteer',
    'TYPE_EDUCATION': 'education',
    'TYPE_CERTIFICATIONS': 'certifications',
    'TYPE_PROJECTS': 'projects',
    'TYPE_SKILLS': 'skills',
    'TYPE_LINK_BLOCKS': 'links',
  };

  const sectionTitlesMap = useMemo(() => {
    const map: Record<string, string> = {};
    structures.forEach(s => {
      const sectionId = structureTypeToId[s.structure_type];
      if (sectionId) map[sectionId] = s.structure_name;
    });
    return map;
  }, [structures]);

  const getSectionTitle = (sectionId: string, defaultTitle: string): string => {
    return sectionTitlesMap[sectionId] || defaultTitle;
  };

  return (
    <div className="resume-preview resume-bonsai" style={rootStyle}>
      <div className="resume-bonsai__header">
        <div className="resume-bonsai__name-block">
          <h1 className="resume-bonsai__name">
            <EditableField fieldName="firstName" activeField={activeField} onFieldClick={onFieldClick} className="resume-bonsai__firstname">
              {personalInfo.firstName}
            </EditableField>
            <EditableField fieldName="lastName" activeField={activeField} onFieldClick={onFieldClick} className="resume-bonsai__lastname">
              {personalInfo.lastName}
            </EditableField>
          </h1>
        </div>
        <div className="resume-bonsai__title-block">
          {targetJobTitle && (
            <p className="resume-bonsai__job-title">
              <EditableField fieldName="position" activeField={activeField} onFieldClick={onFieldClick}>
                {targetJobTitle}
              </EditableField>
            </p>
          )}
        </div>
      </div>

      <div className="resume-bonsai__row">
        <div className="resume-bonsai__label">CONTACT</div>
        <div className="resume-bonsai__value resume-bonsai__contact">
          {personalInfo.email && (
            <span>
              <EditableField fieldName="email" activeField={activeField} onFieldClick={onFieldClick}>
                {personalInfo.email}
              </EditableField>
            </span>
          )}
          {personalInfo.phone && (
            <>
              <span> · </span>
              <span>
                <EditableField fieldName="phone" activeField={activeField} onFieldClick={onFieldClick}>
                  {personalInfo.phone}
                </EditableField>
              </span>
            </>
          )}
          {links?.find(l => l.type === 'linkedin') && (
            <>
              <span> · </span>
              <span>
                <EditableField fieldName="linkedin" activeField={activeField} onFieldClick={onFieldClick}>
                  LinkedIn
                </EditableField>
              </span>
            </>
          )}
          {links?.find(l => l.type === 'github') && (
            <>
              <span> · </span>
              <span>
                <EditableField fieldName="github" activeField={activeField} onFieldClick={onFieldClick}>
                  GitHub
                </EditableField>
              </span>
            </>
          )}
          {links?.find(l => l.type === 'twitter') && (
            <>
              <span> · </span>
              <span>
                <EditableField fieldName="twitter" activeField={activeField} onFieldClick={onFieldClick}>
                  Twitter
                </EditableField>
              </span>
            </>
          )}
          {links?.find(l => l.type === 'dribbble') && (
            <>
              <span> · </span>
              <span>
                <EditableField fieldName="dribbble" activeField={activeField} onFieldClick={onFieldClick}>
                  Dribbble
                </EditableField>
              </span>
            </>
          )}
          {personalInfo.location && (
            <>
              <span> · </span>
              <span>
                <EditableField fieldName="location" activeField={activeField} onFieldClick={onFieldClick}>
                  {personalInfo.location}
                </EditableField>
              </span>
            </>
          )}
        </div>
      </div>

      {professionalSummary && (
        <div className="resume-bonsai__row">
          <div className="resume-bonsai__label">{getSectionTitle('about', 'PROFESSIONAL SUMMARY')}</div>
          <div className="resume-bonsai__value">
            <EditableBlock fieldName="summary" activeField={activeField} onFieldClick={onFieldClick}>
              <ul className="resume-bonsai__list">
                {professionalSummary.split('\n').filter(line => line.trim()).map((line, index) => (
                  <li key={index}>{line.replace(/^[•\-]\s*/, '')}</li>
                ))}
              </ul>
            </EditableBlock>
          </div>
        </div>
      )}

      {workExperiences && workExperiences.length > 0 && (
        <div className="resume-bonsai__row">
          <div className="resume-bonsai__label">{getSectionTitle('experience', 'EXPERIENCE')}</div>
          <div className="resume-bonsai__value">
            {workExperiences.map((exp: WorkExperience) => (
              <EditableBlock key={exp.uid} fieldName={`experience.${exp.uid}`} activeField={activeField} onFieldClick={onFieldClick} className="resume-bonsai__entry">
                <div className="resume-bonsai__entry-header">
                  <div className="resume-bonsai__entry-left">
                    <EditableField fieldName={`experience.${exp.uid}.job_title`} activeField={activeField} onFieldClick={onFieldClick} className="resume-bonsai__entry-company">
                      {exp.job_title}
                    </EditableField>
                    <EditableField fieldName={`experience.${exp.uid}.employer_name`} activeField={activeField} onFieldClick={onFieldClick} className="resume-bonsai__entry-role">
                      {exp.employer_name}
                    </EditableField>
                  </div>
                  <div className="resume-bonsai__entry-right">
                    <EditableField fieldName={`experience.${exp.uid}.dates`} activeField={activeField} onFieldClick={onFieldClick} className="resume-bonsai__entry-date">
                      {formatDateRange(exp.start_date, exp.end_date, exp.is_current, dateFormat, exp.yearOnly, exp.hideDate)}
                    </EditableField>
                  </div>
                </div>
                {exp.description && (
                  <p className="resume-bonsai__entry-description">
                    <EditableField fieldName={`experience.${exp.uid}.description`} activeField={activeField} onFieldClick={onFieldClick}>
                      {exp.description}
                    </EditableField>
                  </p>
                )}
                {exp.achievements && exp.achievements.length > 0 && (
                  <ul className="resume-bonsai__list">
                    {exp.achievements.map((achievement, idx) => (
                      <li key={idx}>
                        <EditableField fieldName={`experience.${exp.uid}.achievements.${idx}`} activeField={activeField} onFieldClick={onFieldClick}>
                          {achievement}
                        </EditableField>
                      </li>
                    ))}
                  </ul>
                )}
              </EditableBlock>
            ))}
          </div>
        </div>
      )}

      {volunteerExperiences && volunteerExperiences.length > 0 && (
        <div className="resume-bonsai__row">
          <div className="resume-bonsai__label">{getSectionTitle('volunteer', 'VOLUNTEER')}</div>
          <div className="resume-bonsai__value">
            {volunteerExperiences.map((exp: WorkExperience) => (
              <EditableBlock key={exp.uid} fieldName={`volunteer.${exp.uid}`} activeField={activeField} onFieldClick={onFieldClick} className="resume-bonsai__entry">
                <div className="resume-bonsai__entry-header">
                  <div className="resume-bonsai__entry-left">
                    <EditableField fieldName={`volunteer.${exp.uid}.job_title`} activeField={activeField} onFieldClick={onFieldClick} className="resume-bonsai__entry-company">
                      {exp.job_title}
                    </EditableField>
                    <EditableField fieldName={`volunteer.${exp.uid}.employer_name`} activeField={activeField} onFieldClick={onFieldClick} className="resume-bonsai__entry-role">
                      {exp.employer_name}
                    </EditableField>
                  </div>
                  <div className="resume-bonsai__entry-right">
                    <EditableField fieldName={`volunteer.${exp.uid}.dates`} activeField={activeField} onFieldClick={onFieldClick} className="resume-bonsai__entry-date">
                      {formatDateRange(exp.start_date, exp.end_date, exp.is_current, dateFormat, exp.yearOnly, exp.hideDate)}
                    </EditableField>
                  </div>
                </div>
                {exp.achievements && exp.achievements.length > 0 && (
                  <ul className="resume-bonsai__list">
                    {exp.achievements.map((achievement, idx) => (
                      <li key={idx}>
                        <EditableField fieldName={`volunteer.${exp.uid}.achievements.${idx}`} activeField={activeField} onFieldClick={onFieldClick}>
                          {achievement}
                        </EditableField>
                      </li>
                    ))}
                  </ul>
                )}
              </EditableBlock>
            ))}
          </div>
        </div>
      )}

      {educations && educations.length > 0 && (
        <div className="resume-bonsai__row">
          <div className="resume-bonsai__label">{getSectionTitle('education', 'EDUCATION')}</div>
          <div className="resume-bonsai__value">
            {educations.map((edu: Education) => (
              <EditableBlock key={edu.uid} fieldName={`education.${edu.uid}`} activeField={activeField} onFieldClick={onFieldClick} className="resume-bonsai__entry resume-bonsai__entry--compact">
                <div className="resume-bonsai__entry-header">
                  <div className="resume-bonsai__entry-left">
                    <EditableField fieldName={`education.${edu.uid}.institution`} activeField={activeField} onFieldClick={onFieldClick} className="resume-bonsai__entry-company">
                      {edu.institution}
                    </EditableField>
                    {edu.degree && (
                      <EditableField fieldName={`education.${edu.uid}.degree`} activeField={activeField} onFieldClick={onFieldClick} className="resume-bonsai__entry-role">
                        {edu.degree}
                      </EditableField>
                    )}
                  </div>
                  <div className="resume-bonsai__entry-right">
                    {edu.graduation_date && (
                      <EditableField fieldName={`education.${edu.uid}.graduation_date`} activeField={activeField} onFieldClick={onFieldClick} className="resume-bonsai__entry-date">
                        Graduation: {formatDateWithFormat(edu.graduation_date, dateFormat)}
                      </EditableField>
                    )}
                  </div>
                </div>
                {edu.field_of_study && (
                  <p className="resume-bonsai__entry-subtitle">
                    <EditableField fieldName={`education.${edu.uid}.field_of_study`} activeField={activeField} onFieldClick={onFieldClick}>
                      {edu.field_of_study}
                    </EditableField>
                  </p>
                )}
                {edu.grade && (
                  <p className="resume-bonsai__entry-subtitle">
                    <EditableField fieldName={`education.${edu.uid}.grade`} activeField={activeField} onFieldClick={onFieldClick}>
                      GPA: {edu.grade}
                    </EditableField>
                  </p>
                )}
                {edu.description && (
                  <p className="resume-bonsai__entry-subtitle">
                    <EditableField fieldName={`education.${edu.uid}.description`} activeField={activeField} onFieldClick={onFieldClick}>
                      {edu.description}
                    </EditableField>
                  </p>
                )}
              </EditableBlock>
            ))}
          </div>
        </div>
      )}

      {certifications && certifications.length > 0 && (
        <div className="resume-bonsai__row">
          <div className="resume-bonsai__label">{getSectionTitle('certifications', 'CERTIFICATIONS')}</div>
          <div className="resume-bonsai__value">
            {certifications.map((cert: Certification) => (
              <EditableBlock key={cert.uid} fieldName={`certifications.${cert.uid}`} activeField={activeField} onFieldClick={onFieldClick} className="resume-bonsai__entry resume-bonsai__entry--compact">
                <div className="resume-bonsai__entry-header">
                  <div className="resume-bonsai__entry-left">
                    <EditableField fieldName={`certifications.${cert.uid}.certification_name`} activeField={activeField} onFieldClick={onFieldClick} className="resume-bonsai__entry-company">
                      {cert.certification_name}
                    </EditableField>
                  </div>
                  <div className="resume-bonsai__entry-right">
                    {(cert.issued_at || cert.organization_name || cert.expires_at || cert.credential_id) && (
                      <span className="resume-bonsai__entry-date">
                        {cert.issued_at && (
                          <EditableField fieldName={`certifications.${cert.uid}.issued_at`} activeField={activeField} onFieldClick={onFieldClick}>
                            Issued: {formatDateWithFormat(cert.issued_at, dateFormat)}
                          </EditableField>
                        )}
                        {cert.organization_name && (
                          <>
                            {cert.issued_at && ' | '}
                            <EditableField fieldName={`certifications.${cert.uid}.organization_name`} activeField={activeField} onFieldClick={onFieldClick}>
                              {cert.organization_name}
                            </EditableField>
                          </>
                        )}
                        {cert.expires_at && (
                          <>
                            {(cert.issued_at || cert.organization_name) && ' | '}
                            <EditableField fieldName={`certifications.${cert.uid}.expires_at`} activeField={activeField} onFieldClick={onFieldClick}>
                              Expires: {formatDateWithFormat(cert.expires_at, dateFormat)}
                            </EditableField>
                          </>
                        )}
                        {cert.credential_id && (
                          <>
                            {(cert.issued_at || cert.organization_name || cert.expires_at) && ' | '}
                            <EditableField fieldName={`certifications.${cert.uid}.credential_id`} activeField={activeField} onFieldClick={onFieldClick}>
                              ID: {cert.credential_id}
                            </EditableField>
                          </>
                        )}
                      </span>
                    )}
                  </div>
                </div>
                {cert.description && (
                  <p className="resume-bonsai__entry-subtitle">
                    <EditableField fieldName={`certifications.${cert.uid}.description`} activeField={activeField} onFieldClick={onFieldClick}>
                      {cert.description}
                    </EditableField>
                  </p>
                )}
              </EditableBlock>
            ))}
          </div>
        </div>
      )}

      {links && links.length > 0 && (
        <div className="resume-bonsai__row">
          <div className="resume-bonsai__label">{getSectionTitle('links', 'LINKS')}</div>
          <div className="resume-bonsai__value resume-bonsai__links">
            {links.filter(l => l.url && l.url.trim()).map((link, idx, arr) => (
              <span key={idx}>
                <EditableField fieldName={`links.${idx}.type`} activeField={activeField} onFieldClick={onFieldClick}>
                  <strong>{link.type}</strong>
                </EditableField>
                {' · '}
                <EditableField fieldName={`links.${idx}.url`} activeField={activeField} onFieldClick={onFieldClick}>
                  {link.url}
                </EditableField>
                {idx < arr.length - 1 && ' | '}
              </span>
            ))}
          </div>
        </div>
      )}

      {categorySkills && Object.values(categorySkills).some(skillsList => skillsList && skillsList.length > 0) && (
        <div className="resume-bonsai__row">
          <div className="resume-bonsai__label">{getSectionTitle('skills', 'SKILLS')}</div>
          <div className="resume-bonsai__value resume-bonsai__skills">
            {Object.entries(categorySkills).map(([category, skills]) => (
              skills && skills.length > 0 && (
                <p key={category} className="resume-bonsai__skill-line">
                  <EditableField fieldName={`skills.category.${category}`} activeField={activeField} onFieldClick={onFieldClick}>
                    {category !== 'General skills' && <strong>{category}: </strong>}
                    {skills.join(' · ')}
                  </EditableField>
                </p>
              )
            ))}
          </div>
        </div>
      )}

      {projects && projects.length > 0 && (
        <div className="resume-bonsai__row">
          <div className="resume-bonsai__label">{getSectionTitle('projects', 'PROJECTS')}</div>
          <div className="resume-bonsai__value">
            {projects.map((project: Project) => {
              const dateRange = project.start_date
                ? (project.is_current
                    ? `${formatDateWithFormat(project.start_date, dateFormat)} - Present`
                    : project.end_date
                      ? `${formatDateWithFormat(project.start_date, dateFormat)} - ${formatDateWithFormat(project.end_date, dateFormat)}`
                      : formatDateWithFormat(project.start_date, dateFormat))
                : '';
              return (
                <EditableBlock key={project.uid} fieldName={`projects.${project.uid}`} activeField={activeField} onFieldClick={onFieldClick} className="resume-bonsai__entry">
                  <div className="resume-bonsai__entry-header">
                    <div className="resume-bonsai__entry-left">
                      <EditableField fieldName={`projects.${project.uid}.project_name`} activeField={activeField} onFieldClick={onFieldClick} className="resume-bonsai__entry-company">
                        {project.project_name}
                      </EditableField>
                      {project.url && (
                        <EditableField fieldName={`projects.${project.uid}.url`} activeField={activeField} onFieldClick={onFieldClick} className="resume-bonsai__entry-role">
                          {project.url}
                        </EditableField>
                      )}
                    </div>
                    {dateRange && (
                      <div className="resume-bonsai__entry-right">
                        <span className="resume-bonsai__entry-date">{dateRange}</span>
                      </div>
                    )}
                  </div>
                  {project.description && (
                    <p className="resume-bonsai__entry-subtitle">
                      <EditableField fieldName={`projects.${project.uid}.description`} activeField={activeField} onFieldClick={onFieldClick}>
                        {project.description}
                      </EditableField>
                    </p>
                  )}
                  {project.achievements && project.achievements.length > 0 && (
                    <ul className="resume-bonsai__list">
                      {project.achievements.map((achievement, idx) => (
                        <li key={idx}>{achievement}</li>
                      ))}
                    </ul>
                  )}
                </EditableBlock>
              );
            })}
          </div>
        </div>
      )}

      {customSections?.map((cs) => (
        <div key={cs.id} className="resume-bonsai__row">
          <div className="resume-bonsai__label">{cs.title.toUpperCase()}</div>
          <div className="resume-bonsai__value">
            {cs.layout === 'stacked' ? (
              cs.entries.map((entry) => (
                <div key={entry.id} className="resume-bonsai__entry">
                  <div className="resume-bonsai__entry-header">
                    <div className="resume-bonsai__entry-left">
                      <span className="resume-bonsai__entry-company">{entry.title}</span>
                    </div>
                    {entry.date && !entry.hideDate && (
                      <div className="resume-bonsai__entry-right">
                        <span className="resume-bonsai__entry-date">
                          {entry.yearOnly
                            ? new Date(entry.date).getFullYear()
                            : formatDateWithFormat(entry.date, dateFormat)}
                          {entry.endDate && !entry.hideEndDate && (
                            <> - {entry.yearOnly
                              ? new Date(entry.endDate).getFullYear()
                              : formatDateWithFormat(entry.endDate, dateFormat)}</>
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                  {entry.description && (
                    <p className="resume-bonsai__entry-subtitle">{entry.description}</p>
                  )}
                </div>
              ))
            ) : (
              <ul className="resume-bonsai__list">
                {cs.entries.map((entry) => (
                  <li key={entry.id}>
                    <strong>{entry.title}</strong>
                    {entry.description && `: ${entry.description}`}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};
